import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { allApprovalUsersNotApproversSelector, approversSelector } from 'domain/approvals/selectors';

import Dialog from 'components/mui/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import UserField from 'pages/common/Dialog/DialogBulkReplaceApprover/components/UserField';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';

import { replaceApproverSchema, ReplaceApproverFormData } from 'pages/common/Dialog/DialogBulkReplaceApprover/schema';

export interface DialogBulkReplaceApproverProps {
  onClose: () => void;
  open?: boolean;
  onSubmit: (data: ReplaceApproverFormData) => Promise<void>;
  fromOptions: Array<string>;
  fromDisabled?: boolean;
  fromValue?: string | null;
  propagateToOtherCompanies?: boolean;
}

const DialogBulkReplaceApprover: React.FC<DialogBulkReplaceApproverProps> = ({
  onClose,
  onSubmit,
  fromOptions = [],
  fromDisabled = false,
  fromValue,
  propagateToOtherCompanies = false,
  open = false,
}) => {
  const { formatMessage } = useIntl();
  const allApprovers = useSelector(approversSelector).toJS();
  const allApprovalUsersNotApprovers = useSelector(allApprovalUsersNotApproversSelector).toJS();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm<ReplaceApproverFormData>({
    resolver: zodResolver(replaceApproverSchema),
    defaultValues: {
      replace: fromValue ? fromValue : null,
      to: null,
      ...(propagateToOtherCompanies ? { replace_in_companies: false } : {}),
    },
    mode: 'onChange',
  });

  const allApproversOptions = useMemo(
    () => allApprovers.sort((a, b) => a.fullName.localeCompare(b.fullName)).map(({ username }) => username),
    [allApprovers],
  );

  const allApprovalUsersOptions = useMemo(
    () =>
      allApprovalUsersNotApprovers.sort((a, b) => a.fullName.localeCompare(b.fullName)).map(({ username }) => username),
    [allApprovalUsersNotApprovers],
  );

  const emails = useMemo(
    () => [...new Set([...allApproversOptions, ...allApprovalUsersOptions])],
    [allApprovalUsersOptions, allApproversOptions],
  );

  const memoApprovers = useMemo(
    () => ({
      ...allApprovers.reduce((res, approver) => ({ ...res, [approver.username]: { ...approver, group: 'all' } }), {}),
      ...allApprovalUsersNotApprovers.reduce(
        (res, approver) => ({
          ...res,
          [approver.username]: { ...approver, disabled: true, group: 'inactiveUsers' },
        }),
        {},
      ),
    }),
    [allApprovalUsersNotApprovers, allApprovers],
  );

  const handleFormSubmit = async (data: ReplaceApproverFormData) => {
    await onSubmit(data);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={formatMessage({ id: 'form.dialog.replaceApprover.title', defaultMessage: 'Replace approver' })}
      maxWidth="sm"
      withActions={false}
      withContent={false}
    >
      <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
        <DialogContent sx={{ pt: 0 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>
              {formatMessage({
                id: 'form.dialog.replaceApprover.alert.title',
                defaultMessage: 'The approver will not be replaced in the following cases:',
              })}
            </AlertTitle>
            <Box component="ul" sx={{ listStyleType: 'disc', pl: 2 }}>
              <li>
                {formatMessage({
                  id: 'form.dialog.replaceApprover.alert.list.item1',
                  defaultMessage: 'The approver already approved/rejected the document',
                })}
              </li>
              <li>
                {formatMessage({
                  id: 'form.dialog.replaceApprover.alert.list.item2',
                  defaultMessage:
                    'The approval process  associated with the documents is based on "Advance Approval Group"',
                })}
              </li>
            </Box>
          </Alert>
          <Stack direction="column" spacing={1}>
            <Controller
              name="replace"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <UserField
                  options={fromOptions}
                  approvers={memoApprovers}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  textFieldProps={{
                    label: formatMessage({ id: 'form.dialog.replaceApprover.replace', defaultMessage: 'Replace' }),
                    autoFocus: !fromDisabled,
                    error: !!errors.replace,
                    helperText: errors.replace?.message,
                    required: true,
                  }}
                  autocompleteProps={{
                    fullWidth: true,
                    disabled: fromDisabled,
                    id: 'autocomplete-from',
                  }}
                />
              )}
            />
            <Controller
              name="to"
              control={control}
              render={({ field: { onChange, value, onBlur } }) => (
                <UserField
                  options={emails}
                  approvers={memoApprovers}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  textFieldProps={{
                    label: formatMessage({ id: 'form.dialog.replaceApprover.with', defaultMessage: 'With' }),
                    error: !!errors.to,
                    helperText: errors.to?.message,
                    autoFocus: fromDisabled,
                    required: true,
                  }}
                  autocompleteProps={{
                    fullWidth: true,
                    id: 'autocomplete-to',
                  }}
                />
              )}
            />
            {propagateToOtherCompanies && (
              <Controller
                name="replace_in_companies"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label={formatMessage({
                      id: 'form.dialog.replaceApprover.propagateToOtherCompanies',
                      defaultMessage: 'Apply this change across all companies in the organization',
                    })}
                  />
                )}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={onClose}>
            {formatMessage({
              id: 'button.cancel',
              defaultMessage: 'Cancel',
            })}
          </Button>
          <Button variant="contained" type="submit" disabled={!isValid || isSubmitting}>
            {formatMessage({
              id: 'button.replace',
              defaultMessage: 'Replace',
            })}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default DialogBulkReplaceApprover;
